import { useState, useEffect } from 'react'
import { useRegVerStart } from './_hooks/useVerificationStart'
import { useRegVerVerify } from './_hooks/useVerificationVerify'
import { useApiClientStore } from '~/@utils/api/apiClientStore'
import { useNavigate } from 'react-router-dom'

interface UsePhoneRegisterVerificationProps {
  onSuccess?: (response: any) => void
}

export const usePhoneRegisterVerification = ({
  onSuccess,
}: UsePhoneRegisterVerificationProps = {}) => {
  const { verifyStart, isLoading: isStartLoading } = useRegVerStart()
  const { verifyVerify, isLoading: isVerifyLoading } = useRegVerVerify()

  const navigate = useNavigate()

  const apiClient = useApiClientStore.getState().apiClient

  const [isVerifyingPhone, setIsVerifyingPhone] = useState(false)
  const [phone, setPhone] = useState('')
  const [requestId, setRequestId] = useState('')

  const [verificationCode, setVerificationCode] = useState('')
  const [wasResponseSuccess, setWasResponseSuccess] = useState(false)
  const [wasResponseError, setWasResponseError] = useState(false)

  const [waiting, setWaiting] = useState(true)
  const [secondsLeft, setSecondsLeft] = useState(60)

  useEffect(() => {
    if (!waiting || !isVerifyingPhone) return

    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          setWaiting(false)
          return 60
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [waiting, isVerifyingPhone])

  const startVerification = async (phoneNumber: string) => {
    setPhone(phoneNumber)
    setIsVerifyingPhone(true)
    setVerificationCode('')
    setWasResponseSuccess(false)
    setWasResponseError(false)
    setWaiting(true)
    setSecondsLeft(60)

    const response = await verifyStart({
      phoneNumber,
    })

    if (response?.success) {
      setRequestId(response.smsVerificationId)
      return response
    } else {
      setIsVerifyingPhone(false)
      navigate('/account/register')
    }
  }

  const stopVerification = async () => {
    setIsVerifyingPhone(false)
    setPhone('')
    setRequestId('')
    setVerificationCode('')
    setWasResponseSuccess(false)
    setWasResponseError(false)
    setWaiting(false)
  }

  const closeModalAfterSuccess = () => {
    setIsVerifyingPhone(false)
    setPhone('')
    setRequestId('')
    setVerificationCode('')
    setWasResponseSuccess(false)
    setWasResponseError(false)
    setWaiting(false)
  }

  const verifyPhone = async (code: string) => {
    if (!requestId) return

    const response: any = await verifyVerify({
      code: code,
      dataProcessingConsent: true,
      smsVerificationId: requestId,
    })

    if (response.success) {
      apiClient.setAuthToken(response.accessToken)
      setWasResponseSuccess(true)
      setTimeout(() => {
        onSuccess?.(response)
        closeModalAfterSuccess()
      }, 1000)
    } else {
      setWasResponseError(true)
    }
  }

  const handleCodeChange = (newCode: string) => {
    if (newCode.length === 0) {
      setWasResponseSuccess(false)
      setWasResponseError(false)
    }

    setVerificationCode(newCode)

    if (newCode.length === 4) {
      verifyPhone(newCode)
    }
  }

  const handleSendAgainClick = async () => {
    if (!phone) return

    let response: any = await verifyStart({
      phoneNumber: phone,
    })

    if (response?.error) {
      response = await verifyStart({
        phoneNumber: phone,
      })
    }
    if (response?.success) {
      setRequestId(response.smsVerificationId)
      setWaiting(true)
      setSecondsLeft(60)
      setVerificationCode('')
      setWasResponseSuccess(false)
      setWasResponseError(false)
    }
  }

  let verifyCodeInputState = ''
  if (isVerifyLoading) {
    verifyCodeInputState = 'loading'
  } else if (wasResponseSuccess) {
    verifyCodeInputState = 'success'
  } else if (wasResponseError) {
    verifyCodeInputState = 'error'
  }

  return {
    isVerifyingPhone,
    startVerification,
    stopVerification,
    phone,
    verificationCode,
    verifyCodeInputState,
    handleCodeChange,
    handleSendAgainClick,
    waiting,
    secondsLeft,
    isStartLoading,
    isVerifyLoading,
  }
}
